import React, {useState, useMemo, useCallback, useEffect, useRef} from "react";
import Icon from "./Icon";
import AiPropertyCard from "./AiPropertyCard";
import {formatTime, parseMarkdown, sanitizeHtml} from "./Helpers";
import {detectRouteIntent} from "./RouteHelper";
import AreaShapeEditor from "./AreaShapeEditor";
import PriorityPicker from "./PriorityPicker";

/**
 * Renders a single chat turn (user or AI) — sanitized AI markdown replies,
 * inline route/nearby-place widgets detected from message intent, matched
 * property result cards, and dynamically generated follow-up suggestion chips.
 */
function AiMessage({
                       msg,
                       chatId,
                       index,
                       onEdit,
                       onRetry,
                       onCopy,
                       onFollowUp,
                       onMapView,
                       onRoadMapView,
                       isMapOpen,
                       userPosition,
                       prevMessages,
                       compareList = [],
                       onCompare,
                       loading = false,
                       engagementSource,
                       onApplyRelaxation,
                       onLoadMore,
                       answered = false,
                   }) {
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState("");
    const [reaction, setReaction] = useState(null);
    const [activeNearbyChip, setActiveNearbyChip] = useState(null);
    const [activeChipPropertyId, setActiveChipPropertyId] = useState(null);
    // Property-type onboarding stage only (msg.onboardingMultiSelect) --
    // toggled selections staged here until "Continue" is pressed, instead
    // of every other select-stage chip's submit-immediately-on-click.
    const [selectedOnboardingOptions, setSelectedOnboardingOptions] = useState([]);

    const isUser = msg.role === "user";
    const isAi = !isUser;

    const lastProps = useMemo(() => {
        if (!prevMessages?.length) return [];
        for (let i = prevMessages.length - 1; i >= 0; i--) {
            const m = prevMessages[i];
            if (m.role === "ai" && m.properties?.length > 0) return m.properties;
        }
        return [];
    }, [prevMessages]);

    const safeHtml = useMemo(() => {
        if (!isAi || !msg.text) return "";
        const raw = parseMarkdown(msg.text);
        return sanitizeHtml(raw);
    }, [isAi, msg.text]);

    const routeIntent = useMemo(
        () => detectRouteIntent(msg.text),
        [msg.text]
    );

    const followUps = useMemo(() => {
        if (!isAi || msg.isError) return [];
        if (msg.followUps?.length) return msg.followUps;
        if (!msg.hasResults || !msg.properties?.length) return [];

        const props = msg.properties;
        const chips = [];
        const types = [...new Set(props.map(p => p.type).filter(Boolean))];
        const hasSold = props.some(p => p.soldOut);
        const hasRera = props.some(p => p.reraApproved);
        const locs = [...new Set(props.map(p => p.location?.split(",")[0]).filter(Boolean))];
        const minPrice = Math.min(...props.map(p => p.priceRaw ?? 0).filter(Boolean));

        if (hasSold) chips.push("Show only available properties");
        if (!hasRera) chips.push("Show only RERA approved");
        if (locs.length === 1) chips.push(`More properties in ${locs[0]}`);
        if (types.includes("Apartment") && !types.includes("Villa"))
            chips.push("Show villas in same budget");
        if (types.includes("Villa"))
            chips.push("Show apartments instead");
        if (minPrice > 0) chips.push("Show cheaper options under " + formatBudgetChip(minPrice * 0.75));

        return chips.slice(0, 4);
    }, [isAi, msg.isError, msg.hasResults, msg.properties, msg.followUps]);

    function formatBudgetChip(price) {
        if (!price || isNaN(price)) return "";
        if (price >= 10_000_000) {
            const cr = price / 10_000_000;
            return cr === Math.floor(cr) ? `₹${cr} Cr` : `₹${cr.toFixed(1)} Cr`;
        }
        const l = Math.round(price / 100_000) * 100_000 / 100_000;
        return `₹${l} L`;
    }

    const resolveLocationString = useCallback((locStr) => {
        if (!locStr) return null;
        const lower = locStr.toLowerCase();
        if (lower.includes("1st") || lower.includes("1 st") || lower.includes("first") || lower.includes("above") || lower.includes("this")) {
            return lastProps[0]?.location || lastProps[0]?.title || locStr;
        }
        if (lower.includes("2nd") || lower.includes("2 nd") || lower.includes("second")) {
            return lastProps[1]?.location || lastProps[1]?.title || locStr;
        }
        return locStr;
    }, [lastProps]);

    const resolvedRouteOrigin = useMemo(() => {
        if (!routeIntent) return null;
        if (routeIntent.usePropAsOrigin) return lastProps[0]?.location || lastProps[0]?.title || routeIntent.origin;
        return resolveLocationString(routeIntent.origin);
    }, [routeIntent, resolveLocationString, lastProps]);

    const resolvedRouteDestination = useMemo(() => {
        if (!routeIntent) return null;
        return resolveLocationString(routeIntent.destination);
    }, [routeIntent, resolveLocationString]);

    const saveEdit = useCallback(() => {
        if (editText.trim() && onEdit) onEdit(chatId, msg.id, editText.trim());
        setEditing(false);
        setEditText("");
    }, [editText, onEdit, chatId, msg.id]);

    const handleReaction = useCallback((type) => {
        setReaction((prev) => prev === type ? null : type);
    }, []);

    return (
        <div className={`message-row ${isUser ? "user" : "ai"}`}>
            <div className={`msg-bubble ${msg.isError ? "error" : ""} ${isAi && msg.hasResults ? "wide" : ""}`}>

                {editing ? (
                    <>
                <textarea
                    className="msg-edit-area"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    autoFocus
                    aria-label="Edit your message"
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            saveEdit();
                        }
                        if (e.key === "Escape") {
                            setEditing(false);
                            setEditText("");
                        }
                    }}
                />
                        <div className="msg-edit-btns">
                            <button className="edit-save" onClick={saveEdit}>Save</button>
                            <button onClick={() => {
                                setEditing(false);
                                setEditText("");
                            }}>Cancel
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {isAi && msg.isInlineOnboarding && msg.onboardingStageIndex && msg.onboardingTotalStages && (
                            <div className="msg-onboarding-progress">
                                Question {msg.onboardingStageIndex} of {msg.onboardingTotalStages}
                            </div>
                        )}

                        {isAi && msg.hasResults && msg.properties?.length > 0
                            ? <div className={`msg-text-content${msg.keepIntroList ? "" : " msg-text-intro"}`}
                                   dangerouslySetInnerHTML={{__html: safeHtml}}/>
                            : isAi
                                ? <div className={`msg-text-content ${
                                    msg.isLocationAgent ? "msg-location-agent" : ""
                                }`} dangerouslySetInnerHTML={{__html: safeHtml}}/>
                                : <div>{msg.text}</div>
                        }

                        {isUser && routeIntent && resolvedRouteOrigin && resolvedRouteDestination && (
                            <InlineRouteMap
                                origin={resolvedRouteOrigin}
                                destination={resolvedRouteDestination}
                            />
                        )}


                        {isAi && msg.isRoadSearch && msg.roadPolyline && (
                            <button className="msg-route-btn"
                                    onClick={() => onRoadMapView?.(msg.properties || [], {
                                        roadName:     msg.roadName,
                                        roadPolyline: msg.roadPolyline,
                                        bufferKm:     msg.bufferKm,
                                    })}
                                    aria-label={`Show ${msg.roadName || "road"} corridor on map`}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                                    <path d="M4 20 L10 4 L14 4 L20 20"/>
                                    <path d="M8 14h8" strokeDasharray="2 2"/>
                                </svg>
                                Show {msg.roadName ? `${msg.roadName} ` : ""}Corridor
                                {msg.bufferKm ? ` (±${msg.bufferKm} km)` : ""} on Map
                            </button>
                        )}

                        {isAi && msg.hasResults && msg.properties?.length > 0 && (
                            <div className="ai-property-results-list" role="list">
                                {msg.properties.map((property) => (
                                    <PropertyCardWithChips
                                        key={property.id}
                                        property={property}
                                        onMapView={onMapView}
                                        onFollowUp={onFollowUp}
                                        isMapOpen={isMapOpen}
                                        userPosition={userPosition}
                                        allProperties={msg.properties}
                                        activeNearbyChip={activeNearbyChip}
                                        setActiveNearbyChip={setActiveNearbyChip}
                                        activeChipPropertyId={activeChipPropertyId}
                                        setActiveChipPropertyId={setActiveChipPropertyId}
                                        isInCompare={compareList.some(p => p.id === property.id)}
                                        onCompare={onCompare}
                                        engagementSource={engagementSource}
                                    />
                                ))}
                            </div>
                        )}

                        {isAi && msg.hasResults && msg.nextCursor != null && onLoadMore && (
                            <div className="msg-load-more">
                                <button type="button" className="msg-load-more-btn"
                                        disabled={msg.loadingMore}
                                        onClick={() => onLoadMore(msg)}>
                                    {msg.loadingMore ? "Loading…" : "Load more"}
                                </button>
                                {msg.orderedCount != null && (
                                    <span className="msg-load-more-count">
                                        Showing {msg.properties.length} of {msg.orderedCount}
                                    </span>
                                )}
                            </div>
                        )}

                        {isAi && msg.relaxationOptions?.length > 0 && (
                            <div className="msg-relaxation-options" role="group" aria-label="Other ways to find more matches">
                                <div className="msg-followups-label">
                                    {msg.relaxationApplied
                                        ? "Applied"
                                        : msg.hasResults
                                            ? "Or try a different trade-off"
                                            : onApplyRelaxation ? "Tap one to try it" : "This would find more matches"}
                                </div>
                                {msg.relaxationOptions.map((option) => (
                                    <button
                                        key={option.dimension}
                                        type="button"
                                        className="msg-relaxation-chip"
                                        disabled={!onApplyRelaxation || msg.relaxationApplied}
                                        title={
                                            msg.relaxationApplied === option.dimension ? "Already applied — see the reply below"
                                                : msg.relaxationApplied ? "Another option was applied — see the reply below"
                                                : onApplyRelaxation ? `Apply: ${option.label}` : option.label
                                        }
                                        onClick={() => onApplyRelaxation?.(option.dimension, option.label, msg.id)}
                                    >
                                        {option.label} — {option.resultCount} {option.resultCount === 1 ? "match" : "matches"}
                                        {msg.relaxationApplied === option.dimension && " ✓"}
                                    </button>
                                ))}
                            </div>
                        )}

                        {isAi && msg.onboardingOptions?.length > 0 && (
                            msg.onboardingMultiSelect ? (
                                <div className="msg-followups msg-followups-multi" role="group" aria-label="Choose one or more answers">
                                    <div className="msg-followups-label">Choose one or more, or type your own</div>
                                    {msg.onboardingOptions.map((s, i) => {
                                        const isSelected = selectedOnboardingOptions.includes(s);
                                        return (
                                            <button key={i}
                                                    className={`msg-followup-btn msg-followup-btn-selectable${isSelected ? " selected" : ""}`}
                                                    aria-pressed={isSelected}
                                                    disabled={answered}
                                                    onClick={() => setSelectedOnboardingOptions((prev) =>
                                                        isSelected ? prev.filter((o) => o !== s) : [...prev, s]
                                                    )}
                                                    title={s}>
            <span className="msg-followup-icon" aria-hidden="true">
              {isSelected ? "✓" : getFollowUpIcon(s)}
            </span>
                                                {s}
                                            </button>
                                        );
                                    })}
                                    <button className="msg-followup-btn msg-followup-continue-btn"
                                            disabled={answered || selectedOnboardingOptions.length === 0}
                                            onClick={() => onFollowUp?.(selectedOnboardingOptions.join(", "))}>
                                        Continue{selectedOnboardingOptions.length > 0 ? ` (${selectedOnboardingOptions.length})` : ""}
                                    </button>
                                </div>
                            ) : (
                                <div className="msg-followups" role="group" aria-label="Choose an answer">
                                    <div className="msg-followups-label">Choose one, or type your own</div>
                                    {msg.onboardingOptions.map((s, i) => (
                                        <button key={i} className="msg-followup-btn"
                                                disabled={answered}
                                                onClick={() => onFollowUp?.(s)} title={s}>
            <span className="msg-followup-icon" aria-hidden="true">
              {getFollowUpIcon(s)}
            </span>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            )
                        )}

                        {isAi && msg.onboardingAreaPins?.length > 0 && msg.onboardingAwaitingConfirmation && (
                            <AreaShapeEditor
                                pins={msg.onboardingAreaPins}
                                disabled={loading}
                                onConfirm={(name, shapes) => onFollowUp?.(name, {
                                    confirmPendingLocation: true, confirmedAreaShapes: shapes,
                                })}
                            />
                        )}

                        {isAi && msg.isPriorityPicker && msg.priorityPickerItems?.length > 0 && (
                            <PriorityPicker
                                items={msg.priorityPickerItems}
                                disabled={loading}
                                onConfirm={(priorities, metricOrder) => onFollowUp?.("Confirmed priorities", {
                                    confirmPriorities: true, requirementPriorities: priorities, metricOrder,
                                })}
                            />
                        )}

                        {followUps.length > 0 && (
                            <div className="msg-followups" role="group" aria-label="Suggested follow-up questions">
                                <div className="msg-followups-label">Explore further</div>
                                {followUps.map((s, i) => (
                                    <button key={i} className="msg-followup-btn"
                                            onClick={() => onFollowUp?.(s)} title={s}>
            <span className="msg-followup-icon" aria-hidden="true">
              {getFollowUpIcon(s)}
            </span>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="msg-meta">
                            {msg.timestamp && (
                                <time className="msg-time" dateTime={new Date(msg.timestamp).toISOString()}>
                                    {formatTime(msg.timestamp)}
                                </time>
                            )}
                            {msg.edited && <span className="msg-edited">edited</span>}
                            {isUser && (
                                <button className="msg-action"
                                        onClick={() => {
                                            setEditing(true);
                                            setEditText(msg.text);
                                        }}
                                        aria-label="Edit message">
                                    <Icon name="edit" size={12} aria-hidden="true"/> Edit
                                </button>
                            )}
                            {isAi && !msg.isInlineOnboarding && (
                                <>
                                    <button className="msg-action" onClick={() => onCopy?.(msg.text)} aria-label="Copy">
                                        <Icon name="copy" size={12} aria-hidden="true"/> Copy
                                    </button>
                                    <button className="msg-action" onClick={() => onRetry?.(chatId, index)}
                                            disabled={loading} aria-label="Retry">
                                        <Icon name="retry" size={12} aria-hidden="true"/> Retry
                                    </button>
                                </>
                            )}
                            {isAi && !msg.isError && !msg.isInlineOnboarding && (
                                <div className="msg-reactions" role="group" aria-label="Rate this response"
                                     style={{marginLeft: "auto", display: "flex", gap: 3}}>
                                    <button className={`msg-reaction-btn ${reaction === "up" ? "active" : ""}`}
                                            onClick={() => handleReaction("up")} aria-pressed={reaction === "up"}
                                            aria-label="Helpful">👍
                                    </button>
                                    <button className={`msg-reaction-btn ${reaction === "down" ? "active" : ""}`}
                                            onClick={() => handleReaction("down")} aria-pressed={reaction === "down"}
                                            aria-label="Not helpful">👎
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}


function getFollowUpIcon(text) {
    const t = text.toLowerCase();
    if (t.includes("rera"))                    return "✅";
    if (t.includes("vastu"))                   return "🕉";
    if (t.includes("villa"))                   return "🏡";
    if (t.includes("apartment") || t.includes("flat")) return "🏢";
    if (t.includes("available") || t.includes("sold")) return "🟢";
    if (t.includes("ready to move"))           return "🔑";
    if (t.includes("under construction"))      return "🏗";
    if (t.includes("bhk"))                     return "🛏";
    if (t.includes("budget") || t.includes("₹") || t.includes("cheap") || t.includes("under"))
        return "💰";
    if (t.includes("rental") || t.includes("invest") || t.includes("yield"))
        return "📈";
    if (t.includes("more") || t.includes("properties in")) return "📍";
    if (t.includes("compare"))                 return "⚖️";
    if (t.includes("commute") || t.includes("drive time") || t.includes("travel time")) return "🚗";
    if (t.includes("neighborhood") || t.includes("area"))  return "🏘️";
    if (t.includes("school"))                  return "🏫";
    if (t.includes("hospital"))                return "🏥";
    if (t.includes("metro") || t.includes("transit"))      return "🚇";
    if (t.includes("nearby") || t.includes("near"))        return "📍";
    if (t.includes("km") || t.includes("radius"))          return "🎯";
    if (t.includes("appreciation") || t.includes("growth")) return "📈";
    if (t.includes("zoning") || t.includes("rera") || t.includes("legal")) return "⚖️";
    if (t.includes("family") || t.includes("livab"))       return "👨‍👩‍👧";
    if (t.includes("mall") || t.includes("grocery") || t.includes("shop")) return "🛒";
    return "🔍";
}

// A bare locality name typed in chat ("hsr", "marathahalli") is too
// ambiguous for Google's Directions API on its own -- it needs the same
// city/country context AiNearbyWrapper's own geocodeAddress already
// appends before resolving a place. Found live: "distance from
// marathahalli to hsr" failed here ("Could not find one of the
// locations") while the exact same query succeeded elsewhere in the
// app that does append that context, producing an inconsistent
// "the map can't find it, but here's your distance anyway" result.
// Skipped when the string already names a locality/city/country of its
// own (comma-separated, or already mentions Bengaluru/Bangalore/
// Karnataka/India) so a fully-qualified address is never double-suffixed.
const withCityContext = (str) => {
    if (/,|bengaluru|bangalore|karnataka|\bindia\b/i.test(str)) return str;
    return `${str}, Bengaluru, India`;
};

const toLatLng = (val) => {
    if (!val) return null;
    const G = window.google.maps;
    if (typeof val === "object" && val.lat != null)
        return new G.LatLng(parseFloat(val.lat), parseFloat(val.lng));
    return withCityContext(String(val));
};

function InlineRouteMap({origin, destination}) {
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    const dirRef = useRef(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState("DRIVING");

    const MODES = [
        {key: "DRIVING", label: "Drive", icon: "🚗"},
        {key: "TRANSIT", label: "Transit", icon: "🚌"},
        {key: "WALKING", label: "Walk", icon: "🚶"},
    ];

    const renderRoute = useCallback((map, travelMode) => {
        if (!map || !origin || !destination) return;
        const G = window.google.maps;
        if (dirRef.current) {
            dirRef.current.setMap(null);
            dirRef.current = null;
        }
        const renderer = new G.DirectionsRenderer({
            polylineOptions: {strokeColor: "#2563eb", strokeWeight: 4, strokeOpacity: 0.9},
        });
        renderer.setMap(map);
        dirRef.current = renderer;
        const o = toLatLng(origin), d = toLatLng(destination);
        if (!o || !d) {
            setError("Could not resolve location.");
            return;
        }
        new G.DirectionsService().route(
            {origin: o, destination: d, travelMode: G.TravelMode[travelMode]},
            (res, status) => {
                if (status === "OK") {
                    renderer.setDirections(res);
                    const leg = res.routes[0].legs[0];
                    setResult({distance: leg.distance.text, duration: leg.duration.text});
                    setError(null);
                } else {
                    if (status === "REQUEST_DENIED") {
                        setError("Route service requires billing. Try: Show route from [location] to [destination].");
                    } else {
                        setError('Could not calculate route. Try adding city name (e.g. "Marathahalli, Bengaluru").');
                    }
                    setResult(null);
                }
            }
        );
    }, [origin, destination]);

    useEffect(() => {
        if (!origin || !destination) return;
        const waitSDK = () => new Promise((res) => {
            if (typeof window.google?.maps?.Map === "function") {
                res();
                return;
            }
            const t = setInterval(() => {
                if (typeof window.google?.maps?.Map === "function") {
                    clearInterval(t);
                    res();
                }
            }, 100);
        });
        waitSDK().then(() => {
            if (!mapRef.current) return;
            const G = window.google.maps;
            const map = new G.Map(mapRef.current, {
                zoom: 12, center: {lat: 12.9716, lng: 77.5946},
                mapTypeControl: false, streetViewControl: false,
                fullscreenControl: false, zoomControl: true,
            });
            mapInst.current = map;
            renderRoute(map, mode);
        });
        // The map is rebuilt only for a new origin/destination; a travel-mode change re-routes on the
        // existing map (the effect below).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [origin, destination]);

    useEffect(() => {
        if (mapInst.current) renderRoute(mapInst.current, mode);
    }, [mode, renderRoute]);

    return (
        <div className="inline-route-map">
            <div ref={mapRef} className="inline-route-canvas"/>
            <div className="inline-route-modes">
                {MODES.map(({key, label, icon}) => (
                    <button key={key} className={`inline-route-mode${mode === key ? " active" : ""}`}
                            onClick={() => setMode(key)}>{icon} {label}</button>
                ))}
            </div>
            {result && (
                <div className="inline-route-result">
              <span className="inline-route-stat">
                <strong>{result.distance}</strong><span>Distance</span>
              </span>
                    <div className="inline-route-divider"/>
                    <span className="inline-route-stat">
                <strong>{result.duration}</strong>
                <span>{mode === "DRIVING" ? "Drive time" : mode === "TRANSIT" ? "Transit" : "Walk time"}</span>
              </span>
                </div>
            )}
            {error && <div className="inline-route-error">⚠️ {error}</div>}
        </div>
    );
}

function PropertyCardWithChips({
                                   property,
                                   onMapView,
                                   onFollowUp,
                                   isMapOpen,
                                   userPosition,
                                   allProperties,
                                   activeNearbyChip,
                                   setActiveNearbyChip,
                                   activeChipPropertyId,
                                   setActiveChipPropertyId,
                                   isInCompare = false,
                                   onCompare,
                                   engagementSource,
                               }) {
    return (
        <div className="pcard-with-chips" role="listitem">
            <AiPropertyCard
                property={property}
                onMapView={() => onMapView?.([property])}
                isMapOpen={isMapOpen}
                userPosition={userPosition}
                isInCompare={isInCompare}
                onCompare={onCompare}
                engagementSource={engagementSource}
            />
        </div>
    );
}

export default React.memo(AiMessage);
